import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Grid, IconButton, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useMemo, useState } from 'react';
import { EmailLog } from 'wisestep-api';
import {
  getDateUsingFormat,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../context';
import CVViewModal from '../../../../features/conversationWidget/components/CVViewModal';
import { IOpenCvView } from '../../../../features/conversationWidget/components/SubmissionForm';
import { downloadCV } from '../../../../features/conversationWidget/utils/cvHelper';
import WsAvatar from '../../../../features/teams/components/WsAvatar';
import PersonAvatar from '../../../../images/Avatar/Person.svg';
import { stripHtmlUsingRegex } from '../../util/JobCampaignEmailHelper';

import { EmailReply } from './EmailReply';

interface IEmailBodyProps {
  emailLog: EmailLog;
  defaultExpand?: boolean;
  recruiterAccess?: boolean;
  showReply: boolean;
}

export const EmailBody: React.FC<IEmailBodyProps> = ({
  emailLog,
  defaultExpand = false,
  recruiterAccess = false,
  showReply,
}) => {
  const [expand, setExpand] = useState<boolean>(defaultExpand);
  const workspace = useWorkspaceContext();
  const [openCvView, setOpenCvView] = useState<IOpenCvView>({
    open: false,
    url: '',
  });
  const resumeAction = (action: string, url: string, docName?: string) => {
    switch (action) {
      case 'view':
        setOpenCvView({ open: true, url: url });
        break;
      case 'download':
        if (!isEmpty(url)) {
          url && downloadCV(url, docName);
        }
        break;
    }
  };

  const [isQuoteHidden, setIsQuoteHidden] = useState<boolean>(true);
  const parser = new DOMParser();
  const doc = parser.parseFromString(emailLog.body ?? '', 'text/html');
  const quoteDiv = doc.querySelector('div.gmail_quote.gmail_quote_container') as HTMLElement;
  const trimmedContent = useMemo(() => {
    if (quoteDiv?.parentNode) {
      quoteDiv.parentNode.removeChild(quoteDiv);
    }
    return doc.body.innerHTML;
  }, [doc.body, quoteDiv]);

  const toggleQuote = () => setIsQuoteHidden((prev) => !prev);
  return (
    <Grid container display="flex" alignItems="center" rowGap={1}>
      <Grid
        item
        xs={12}
        display="flex"
        alignItems="flex-start"
        onClick={() => setExpand((prev) => !prev)}
        sx={{
          cursor: 'pointer',
        }}
      >
        <Grid xs={8} item container display="flex" alignItems="center" columnGap={1}>
          <WsAvatar src={PersonAvatar} />
          <Grid item>
            <Grid item container display="flex" alignItems="center" columnGap={0.5}>
              <WsTypography bold cursor="pointer">
                {emailLog?.from ?? 'Not Available'}
              </WsTypography>
              <WsTypography variant={WsTypoVariants.BODY2} opacity cursor="pointer">{`<${
                emailLog?.from ?? ''
              }>`}</WsTypography>
            </Grid>
            <WsTypography variant={WsTypoVariants.BODY2} opacity={true} cursor="pointer">
              {`To :${emailLog?.to?.[0] ?? ''}`}
            </WsTypography>
          </Grid>
        </Grid>
        <Grid xs={4} display="flex" justifyContent="flex-end">
          <WsTypography variant={WsTypoVariants.BODY2} opacity={true} cursor="pointer">
            {emailLog?.createdOn
              ? getDateUsingFormat(emailLog?.createdOn.toString(), 'MMM D, YYYY, h:mm A')
              : ''}
          </WsTypography>
        </Grid>
      </Grid>
      <Grid item xs={11.5} ml={5}>
        {!expand ? (
          <Typography
            variant="body1"
            color={WsTypoColors.BLACK}
            onClick={() => setExpand((prev) => !prev)}
            sx={{
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {stripHtmlUsingRegex(doc.body.outerHTML)}
          </Typography>
        ) : (
          <Typography
            fontWeight={500}
            dangerouslySetInnerHTML={{
              __html: trimmedContent,
            }}
          />
        )}
      </Grid>
      {expand && (
        <Grid xs={12} ml={5}>
          {quoteDiv && (
            <Grid xs={11}>
              <IconButton onClick={toggleQuote} sx={{ p: 0 }}>
                <MoreHorizIcon sx={{ height: 20, width: 20 }} />
              </IconButton>
              {!isQuoteHidden && (
                <Typography
                  fontWeight={500}
                  dangerouslySetInnerHTML={{
                    __html: quoteDiv.outerHTML,
                  }}
                />
              )}
            </Grid>
          )}
          {(emailLog?.attachments ?? []).map((attachment, index) => (
            <Grid key={index} xs={7} container>
              <Grid item xs={7}>
                <WsTypography variant={WsTypoVariants.BODY1}>
                  {attachment?.attachementName ?? ''}
                </WsTypography>
              </Grid>
              <Grid container item xs={3.5} columnSpacing={2}>
                <Grid item>
                  <FileDownloadOutlinedIcon
                    onClick={() =>
                      resumeAction(
                        'download',
                        attachment.attachementURL,
                        attachment.attachementName ?? ''
                      )
                    }
                    sx={{ color: WsColors.TheBlues500, fontSize: 24, cursor: 'pointer' }}
                  />
                </Grid>
                <Grid item>
                  <VisibilityOutlinedIcon
                    onClick={() => resumeAction('view', attachment.attachementURL)}
                    sx={{ color: WsColors.TheBlues500, fontSize: 24, cursor: 'pointer' }}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}
          {showReply && (
            <Grid xs={11.5} item>
              {(workspace.owns || recruiterAccess) && <EmailReply emailLog={emailLog} />}
            </Grid>
          )}
        </Grid>
      )}
      {openCvView.open && (
        <CVViewModal
          open={openCvView.open}
          s3url={openCvView.url}
          handleClose={() => setOpenCvView({ open: false, url: openCvView.url })}
        />
      )}
    </Grid>
  );
};
